import { Component, input, inject, OnChanges, OnDestroy, OnInit } from '@angular/core';

import { Router } from '@angular/router';

import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { IndustryPackService } from 'app/providers/semantria-api/industry-pack.service';
import { LanguageTemplateService } from 'app/providers/semantria-api/language-template.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { LocalStorageService } from '../../../../services/local-storage.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-analytic-items-summary',
  templateUrl: './configuration-analytic-items-summary.component.html',
  styleUrls: ['./configuration-analytic-items-summary.component.scss'],
  standalone: false,
})
export class ConfigurationAnalyticItemsSummaryComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly selectedConfiguration = input<SemantriaConfiguration>();
  readonly accountLimits = input<{ [key: string]: number }>();
  readonly selectedConfigurationType = input<string>('CO');
  groupId: string;

  isConfigLoading: boolean;
  showAnalyticItemDialog: boolean;

  configurationLimits = {
    Entities: {
      used: -1,
      total: 0,
    },
    Sentiment: {
      used: -1,
      total: 0,
    },
    Queries: {
      used: -1,
      total: 0,
    },
    'Concept Topics': {
      used: -1,
      total: 0,
    },
    Taxonomy: {
      used: -1,
      total: 0,
    },
    Blacklist: {
      used: -1,
      total: 0,
    },
  };

  readonly typeButtons = [
    {
      label: 'Entities',
      i18n: 'entities',
      clickHandler: () => this.gotoConfigurationPage('entities'),
    },
    {
      label: 'Sentiment',
      i18n: 'sentiment',
      clickHandler: () => this.gotoConfigurationPage('sentiment'),
    },
    {
      label: 'Queries',
      i18n: 'queries',
      clickHandler: () => this.gotoConfigurationPage('queries'),
    },
    {
      label: 'Concept Topics',
      i18n: 'topics',
      clickHandler: () => this.gotoConfigurationPage('concept_topics'),
    },
    {
      label: 'Taxonomy',
      i18n: 'taxonomy',
      clickHandler: () => this.gotoConfigurationPage('taxonomy'),
    },
    {
      label: 'Blacklist',
      i18n: 'blacklist',
      clickHandler: () => this.gotoConfigurationPage('blacklist'),
    },
  ];
  private localStorageService = inject(LocalStorageService);
  constructor(
    private groupService: GroupService,
    private industryPackService: IndustryPackService,
    private languageTemplateService: LanguageTemplateService,
    private configurationService: ConfigurationService,
    private router: Router
  ) {}

  async ngOnInit() {
    if (window.addEventListener) {
      window.addEventListener('storage', this.localStorageListener, false);
    }
    this.groupId = this.groupService.getSelectedGroupId(true);
  }

  ngOnChanges() {
    if (this.selectedConfiguration() && this.accountLimits()) {
      this.configurationLimits.Entities.total = this.accountLimits().USER_ENTITIES_LIMIT;
      this.configurationLimits.Sentiment.total = this.accountLimits().SENTIMENT_PHRASES_LIMIT;
      this.configurationLimits.Queries.total = this.accountLimits().QUERY_TOPICS_LIMIT;
      this.configurationLimits['Concept Topics'].total = this.accountLimits().CONCEPT_TOPICS_LIMIT;
      this.configurationLimits.Taxonomy.total = this.accountLimits().TAXONOMY_LIMIT;
      this.configurationLimits.Blacklist.total = this.accountLimits().BLACKLISTS_LIMIT;

      this.refreshItemCount();
    }
  }

  gotoConfigurationPage(fragment: string) {
    this.router.navigate(
      [`/groups/${this.groupId}/configurations/${this.selectedConfiguration().id}`],
      { fragment }
    );
  }

  private localStorageListener = () => {
    if (this.localStorageService.getItem('itemChanged')) {
      this.refreshItemCount();
      this.localStorageService.removeItem('itemChanged');
    }
  };

  //selectedConfigType is always 'CO' for config list details page
  private refreshItemCount() {
    this.isConfigLoading = true;
    if (!this.selectedConfiguration().removed) {
      let configItemsCountPromise;
      switch (this.selectedConfigurationType()) {
        case 'CO':
          configItemsCountPromise = this.configurationService.getConfigurationItemCounts(
            this.selectedConfiguration().id
          );
          break;
        case 'LT':
          configItemsCountPromise = this.languageTemplateService.getConfigurationItemCounts(
            this.selectedConfiguration().id
          );
          break;
        case 'IP':
          configItemsCountPromise = this.industryPackService.getConfigurationItemCounts(
            this.selectedConfiguration().id
          );
          break;
        default:
          break;
      }

      configItemsCountPromise
        .then((res) => {
          this.configurationLimits.Entities.used = res.user_entities_count;
          this.configurationLimits.Sentiment.used = res.sentiment_phrases_count;
          this.configurationLimits.Queries.used = res.query_topic_count;
          this.configurationLimits['Concept Topics'].used = res.concept_topics_count;
          this.configurationLimits.Taxonomy.used = res.taxonomy_count;
          this.configurationLimits.Blacklist.used = res.blacklist_count;
        })
        .finally(() => (this.isConfigLoading = false));
    } else {
      this.isConfigLoading = false;
    }
  }

  onAnalyticItemDialogOpened() {
    this.showAnalyticItemDialog = true;
  }

  onAnalyticItemDialogClosed() {
    this.showAnalyticItemDialog = false;
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.localStorageListener, false);
  }
}
